/**
 * Wall Newspaper — collaboration over WebRTC (PeerJS)
 *
 * Simple model: host owns the project, guests are read-only viewers. Every
 * mutation on the host (pushHistory) broadcasts a fresh project snapshot
 * (no assets — guests see placeholders for missing photos until host re-saves
 * and shares ZIP separately). Future: bi-directional editing with CRDT.
 *
 * Public API (window.WNCollab):
 *   - startHost(callbacks)   → Promise<peerId>    callbacks: { getProject, onPeerJoin, onPeerLeave }
 *   - joinHost(peerId, cb)   → Promise            callbacks: { applyProject, onDisconnect }
 *   - broadcast()                                  host → all guests
 *   - disconnect()
 *   - isActive() / isHost() / peerCount()
 */
(function () {
    'use strict';

    let _peer = null;
    let _hostConn = null;
    const _connections = new Map();
    let _isHost = false;
    let _callbacks = {};

    function ensureDeps() {
        if (!window.Peer)       throw new Error('PeerJS vendor not loaded');
        if (!window.__WebRTC)   throw new Error('webrtc-config not loaded');
    }

    function startHost(callbacks) {
        ensureDeps();
        _callbacks = callbacks || {};
        return new Promise((resolve, reject) => {
            // Use a tool-prefixed random ID so collisions across Timbrica P2P
            // tools never matter on the same PeerJS server.
            const peer = window.__WebRTC.createPeer(null, {});
            peer.on('open', (id) => {
                _peer = peer;
                _isHost = true;
                resolve(id);
            });
            peer.on('error', (err) => {
                if (!_peer) reject(err);
                _callbacks.onError?.(err);
            });
            peer.on('connection', (conn) => {
                conn.on('open', () => {
                    _connections.set(conn.peer, conn);
                    // Send full state to the new guest immediately
                    const project = _callbacks.getProject?.();
                    if (project) {
                        try { conn.send({ type: 'init', project }); }
                        catch (e) { /* noop */ }
                    }
                    _callbacks.onPeerJoin?.(conn.peer);
                });
                conn.on('close', () => {
                    _connections.delete(conn.peer);
                    _callbacks.onPeerLeave?.(conn.peer);
                });
                conn.on('error', () => { _connections.delete(conn.peer); });
            });
        });
    }

    function joinHost(hostId, callbacks) {
        ensureDeps();
        _callbacks = callbacks || {};
        return new Promise((resolve, reject) => {
            const peer = window.__WebRTC.createPeer(null, {});
            let resolved = false;
            const fail = (e) => { if (!resolved) { resolved = true; reject(e); } };
            peer.on('error', fail);
            peer.on('open', () => {
                const conn = peer.connect(hostId, { reliable: true });
                const timer = setTimeout(() => {
                    if (!resolved) fail(new Error('Connection to host timed out'));
                }, 15_000);
                conn.on('open', () => {
                    clearTimeout(timer);
                    _peer = peer;
                    _hostConn = conn;
                    _isHost = false;
                    resolved = true;
                    resolve();
                });
                conn.on('data', (data) => {
                    if (!data || typeof data !== 'object') return;
                    if (data.type === 'init' || data.type === 'update') {
                        _callbacks.applyProject?.(data.project);
                    }
                });
                conn.on('close', () => {
                    _hostConn = null;
                    _callbacks.onDisconnect?.();
                });
                conn.on('error', (e) => fail(e));
            });
        });
    }

    function broadcast() {
        if (!_isHost || !_peer || !_connections.size) return;
        const project = _callbacks.getProject?.();
        if (!project) return;
        const payload = { type: 'update', project };
        for (const conn of _connections.values()) {
            try { conn.send(payload); } catch (e) { /* per-peer fail OK */ }
        }
    }

    function disconnect() {
        _connections.forEach((c) => { try { c.close(); } catch (e) {} });
        _connections.clear();
        if (_hostConn) { try { _hostConn.close(); } catch (e) {} _hostConn = null; }
        if (_peer) { try { _peer.destroy(); } catch (e) {} _peer = null; }
        _isHost = false;
        _callbacks = {};
    }

    window.WNCollab = {
        startHost,
        joinHost,
        broadcast,
        disconnect,
        isActive:  () => _peer != null,
        isHost:    () => _isHost,
        peerCount: () => _connections.size,
    };
})();
